import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    checkIsFormDataValid,
    replaceFilesInFormData,
    getFormattedSettingsFormValue,
    getSettingsFormDataInitialState,
    checkIfDisplayedRequiredFieldsFilled,
} from '../../../../helpers/settingsFormUtils';
import { checkIfFormDataChanged } from '../../../../helpers/checkIfFormDataChanged';

import { defaultErrorMessage } from '../../../../constants/errorMessages';
import { SETTING_ACTION_TYPE } from '../../../../constants/settingsForm';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import SettingsForm from '../../../CustomizeGoalPage/SettingsForm/SettingsForm';
import { Button } from '../../../../design-system';

const MoreInfoModal = ({ type, data, setData, isEditable = false, onClose }) => {
    const [formData, setFormData] = useState(() => {
        const { label, description, ...rest } = data || {};
        const actions = [
            SETTING_ACTION_TYPE.single_input,
            SETTING_ACTION_TYPE.textarea_input,
            SETTING_ACTION_TYPE.richtext_input,
        ];
        if (actions.includes(data.action)) {
            rest.placeholder = 'Add your answer here!';
        }

        return getSettingsFormDataInitialState({ settings: [rest] });
    });
    const originalFormData = useRef(formData);

    const [areChangesToSave, setAreChangesToSave] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const isEditMode = !!data.value;

    useEffect(() => {
        if (!isEditMode) return;

        const isFormDataChanged = checkIfFormDataChanged(originalFormData.current, formData);

        if (isFormDataChanged !== areChangesToSave) {
            setAreChangesToSave(isFormDataChanged);
        }
    }, [formData]);

    const handleSave = async () => {
        try {
            const isFormDataValid = checkIsFormDataValid({
                settingsFormData: formData,
                setSettingsFormData: setFormData,
            });
            if (!isFormDataValid) {
                return;
            }

            setIsLoading(true);

            const updatedFormData = await replaceFilesInFormData(formData);
            const value = getFormattedSettingsFormValue(updatedFormData[0]);
            setData((prev) => ({ ...prev, value }));

            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const allRequiredFieldsFilled = useMemo(
        () => checkIfDisplayedRequiredFieldsFilled({ formData }),
        [formData]
    );

    const buttonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled && (!isEditMode || areChangesToSave)
        ? 'default'
        : 'disabled';

    const title = {
        example: isEditable ? 'Provide an example' : 'View example',
        setting: isEditable ? 'Edit the setting value' : 'View setting value',
        context: isEditable ? 'Provide more context' : 'View context',
    }[type];

    return (
        <Modal size="mediumPlus" onClose={onClose} resetPadding>
            <ModalHeader
                title={title}
                outlined
                customPaddings="px-5 py-3"
                titleStyles="font-heading-bold text-heading-bold-s text-neutral-500"
                onClose={onClose}
            />
            <div className={isEditable ? 'p-5' : 'px-5 pt-5 pb-8'}>
                <div className="mb-3">
                    <p className="font-body-bold text-body-bold-s text-neutral-500 mb-2">
                        <span className="uppercase">{type}: </span>
                        {data.label}
                    </p>

                    <p className="font-body text-body-regular-s text-neutral-500">
                        {data.description}
                    </p>
                </div>

                <SettingsForm
                    formData={formData}
                    setFormData={setFormData}
                    isFormDisabled={!isEditable}
                />
            </div>

            {isEditable && (
                <div className="px-5 py-3 flex justify-end">
                    <Button
                        type="secondary"
                        size="sm"
                        text="Save"
                        state={buttonState}
                        onClick={handleSave}
                    />
                </div>
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default MoreInfoModal;
